
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus, Users, Building2 } from "lucide-react";

interface ComparativaProps {
  mediaIndividual: number;
  mediaEquipo: number;
  mediaOrganizacion: number;
  miembrosEquipo: number;
  totalParticipantesOrganizacion: number;
  diferenciaIndividualEquipo: number;
  diferenciaIndividualOrganizacion: number;
  diferenciaEquipoOrganizacion: number;
}

export function ComparativaIndividualEquipo({ 
  mediaIndividual, 
  mediaEquipo, 
  mediaOrganizacion,
  miembrosEquipo,
  totalParticipantesOrganizacion,
  diferenciaIndividualEquipo,
  diferenciaIndividualOrganizacion,
  diferenciaEquipoOrganizacion
}: ComparativaProps) {
  
  const getTrendIcon = (diferencia: number) => {
    if (diferencia > 5) {
      return <TrendingUp className="h-4 w-4 text-green-500" />;
    } else if (diferencia < -5) {
      return <TrendingDown className="h-4 w-4 text-red-500" />;
    } else {
      return <Minus className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTrendBadge = (diferencia: number, tipo: string) => {
    const diferenciaAbs = Math.abs(diferencia);
    if (diferencia > 5) {
      return (
        <Badge variant="outline" className="text-green-600 border-green-300">
          +{diferenciaAbs.toFixed(1)}% sobre {tipo}
        </Badge>
      );
    } else if (diferencia < -5) {
      return (
        <Badge variant="outline" className="text-red-600 border-red-300">
          -{diferenciaAbs.toFixed(1)}% bajo {tipo}
        </Badge>
      );
    } else {
      return (
        <Badge variant="outline" className="text-gray-600 border-gray-300">
          Similar a {tipo}
        </Badge>
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Comparativa Individual vs Equipo vs Organización
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Niveles de comparación */}
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-gray-600 flex items-center gap-1">
              <Users className="h-3 w-3" />
              Media Individual
            </h4>
            <div className="text-2xl font-bold text-blue-600">
              {mediaIndividual.toFixed(2)}
            </div>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-gray-600 flex items-center gap-1">
              <Users className="h-3 w-3" />
              Media Equipo ({miembrosEquipo} personas)
            </h4>
            <div className="text-2xl font-bold text-purple-600">
              {mediaEquipo.toFixed(2)}
            </div>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-gray-600 flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              Media Organización ({totalParticipantesOrganizacion} personas)
            </h4>
            <div className="text-2xl font-bold text-green-600">
              {mediaOrganizacion.toFixed(2)}
            </div>
          </div>
        </div>
        
        {/* Comparaciones */}
        <div className="space-y-4">
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex items-center gap-2">
              {getTrendIcon(diferenciaIndividualEquipo)}
              <span className="text-sm font-medium">Individual vs Equipo</span>
            </div>
            {getTrendBadge(diferenciaIndividualEquipo, "el equipo")}
          </div>
          
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex items-center gap-2">
              {getTrendIcon(diferenciaIndividualOrganizacion)}
              <span className="text-sm font-medium">Individual vs Organización</span>
            </div>
            {getTrendBadge(diferenciaIndividualOrganizacion, "la organización")}
          </div>
          
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex items-center gap-2">
              {getTrendIcon(diferenciaEquipoOrganizacion)}
              <span className="text-sm font-medium">Equipo vs Organización</span>
            </div>
            {getTrendBadge(diferenciaEquipoOrganizacion, "la organización")}
          </div>
        </div>
        
        {/* Mensajes informativos */}
        {miembrosEquipo === 1 && (
          <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
            ⚠️ Este equipo tiene solo 1 persona. La comparativa individual vs equipo muestra los mismos valores.
          </div>
        )}
        
        {totalParticipantesOrganizacion === 1 && (
          <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
            ⚠️ La organización tiene solo 1 participante. La comparativa muestra los mismos valores.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
